import AppDescription from '@nxsys/components/AppDescription';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import {
  defaultPaymentFrequency,
  getPaymentModelOnboardingLabel,
  paymentModelOptionsIreland,
} from '@nxsys/constants/AppConstants';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import {
  defaultsBusinessTypeOptions,
  titleOptions,
} from '@nxsys/constants/prospect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { prospectActions } from '@nxsys/store/slices/prospect/reducer/prospectSlice';
import { Prospect } from '@nxsys/store/slices/prospect/types';
import { Icons } from '@nxsys/types/icons';
import { Descriptions } from 'antd';
import { find, toNumber } from 'lodash';
import moment from 'moment';
import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ProspectForm, {
  ProspectFormRef,
  ProspectFormValues,
} from './ProspectForm';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';

type ViewProspectProps = {
  isOpen: boolean;
  toggle: () => void;
  data: Prospect | null;
  refetchData: () => void;
  onResendByIds: (ids: number[]) => void;
  onViewOnboarding: (data: Prospect) => void;
  onDeleteProspectById: (id: number) => void;
};

const ViewProspectModal: React.FC<ViewProspectProps> = ({
  isOpen,
  toggle,
  data,
  refetchData,
  onResendByIds,
  onViewOnboarding,
  onDeleteProspectById,
}: ViewProspectProps) => {
  const { messages } = useTypedIntl();

  const { isBureau, payrollCountry, isAgency } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isUK = isBureau && payrollCountry === UNITED_KINGDOM;
  const isIreland = payrollCountry === IRELAND;

  const dispatch = useDispatch<AppDispatch>();
  const appStore = useSelector((state: AppState) => state.appStore);
  // Ireland has its own PAYE/RCT list; RCT is stored as the BE value CIS
  const defaultPaymentModel = isIreland
    ? paymentModelOptionsIreland
    : appStore?.appConfigs?.paymentModels;

  const formRef = useRef<ProspectFormRef>(null);

  // Prospect already has a user account -> form is read-only
  const isReadOnly = !!data?.userRef;

  const [isEditing, setIsEditing] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setIsEditing(isOpen);
  }, [isOpen]);

  const handleSubmit = async (values: ProspectFormValues) => {
    if (!data?.id) return;

    setIsSubmitting(true);
    try {
      const params = {
        DOB: values.dob ? moment(values.dob).toISOString() : null,
        agencyId: !isBureau ? (values.agency?.value ?? null) : null,
        // Business type only belongs to the self-employed models, and neither
        // bureau nor Ireland has one; anything left over from an earlier
        // selection must not be sent
        businessType:
          !isBureau &&
          !isIreland &&
          ['CIS', 'NON_CIS'].includes(values.paymentModel?.value as string)
            ? (values.businessType?.value ?? 'SOLE_TRADER')
            : null,
        companyPaymentId: values.paymentCompany?.value ?? null,
        email: values.email ?? null,
        forename: values.forename ?? null,
        payFrequency: values.payFrequency ?? null,
        paymentModel: isUK
          ? ['PAYEE']
          : values.paymentModel?.value
            ? [values.paymentModel.value]
            : [],
        phone: values.phoneNumber || null,
        surname: values.surname ?? null,
        title: values.title?.value ?? null,
        payRate: !isBureau && values.payRate ? toNumber(values.payRate) : null,
      };
      // unwrap() so a rejected request throws — without it the thunk resolves
      // and the modal closes as if the update had succeeded
      await dispatch(
        prospectActions.updateProspectById({
          id: data.id,
          payload: params,
        }),
      ).unwrap();
      refetchData();
      setIsEditing(false);
      toggle();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const descriptionItems = [
    {
      label: messages['common.title'],
      value:
        find(titleOptions, (opt) => opt.value === data?.title)?.label ?? '',
    },
    {
      label: messages['common.forename'],
      value: data?.forename ?? '',
    },
    {
      label: messages['common.surname'],
      value: data?.surname ?? '',
    },
    {
      label: messages['common.dob'],
      value: data?.DOB ? moment(data.DOB).format('DD/MM/YYYY') : '',
    },
    {
      label: messages['common.email'],
      value: data?.email ?? '',
    },
    {
      label: messages['common.phoneNumber'],
      value: data?.phone ?? '',
    },
    {
      label: isBureau
        ? messages['common.employer']
        : messages['common.paymentCompany'],
      value: data?.companyPayment?.companyName ?? '',
    },
    // An agency user only ever sees their own agency, so the row is noise
    ...(!isBureau && !isAgency
      ? [
          {
            label: messages['common.agencyName'],
            value: data?.agency?.name ?? '',
          },
        ]
      : []),
    {
      label: messages['common.payFrequency'],
      value:
        find(defaultPaymentFrequency, (opt) => opt.value === data?.payFrequency)
          ?.label ?? '',
    },
    ...(!isUK
      ? [
          {
            label: messages['common.paymentModel'],
            value: getPaymentModelOnboardingLabel(
              data?.paymentModel?.[0],
              isIreland,
            ),
          },
          // Ireland has no business type — its CIS value is RCT
          ...(!isBureau &&
          !isIreland &&
          ['CIS', 'NON_CIS'].includes(data?.paymentModel?.[0] as string)
            ? [
                {
                  label: messages['common.businessType'],
                  value:
                    find(
                      defaultsBusinessTypeOptions,
                      (opt) => opt.value === data?.businessType,
                    )?.label ?? '',
                },
              ]
            : []),
        ]
      : []),
    ...(!isBureau
      ? [
          {
            label: messages['common.payRate'],
            value: data?.payRate ?? '',
          },
        ]
      : []),
  ];

  return (
    <AppModalContainer
      title={messages['prospect.viewProspect']}
      isOpen={isOpen}
      toggle={toggle}
      // Unmount on close so unsaved edits are dropped and the next prospect
      // opens with its own values
      destroyOnClose
      actions={
        isEditing
          ? [
              {
                name: messages['common.cancel'],
                variant: 'outlined',
                onClick: () => setIsEditing(false),
              },
              {
                name: messages['common.save'],
                buttonType: 'primary',
                onClick: () => formRef.current?.submit(),
                disabled: isSubmitting || isReadOnly,
                loading: isSubmitting,
              },
            ]
          : [
              {
                name: messages['common.delete'],
                variant: 'outlined',
                className: 'btn-delete',
                onClick: () => {
                  if (data?.id) onDeleteProspectById(data.id);
                  toggle();
                },
                leftIcon: <Icons.IconDelete />,
              },
              ...((data?.userRef && !data?.subcontractor && !isAgency
                ? [
                    {
                      name: messages['prospect.onboarding.resendLoginDetails'],
                      variant: 'outlined',
                      onClick: () => {
                        if (data?.id) onResendByIds([data.id]);
                        toggle();
                      },
                    },
                  ]
                : []) as any),
              ...(!isAgency
                ? [
                    {
                      name: messages['prospect.onboarding.completeOnboarding'],
                      variant: 'outlined',
                      onClick: () => {
                        if (data?.id) onViewOnboarding(data);
                      },
                    },
                  ]
                : []),
              {
                name: messages['common.edit'],
                buttonType: 'primary',
                leftIcon: (
                  <Icons.IconEdit color={defaultTheme.theme.palette.white} />
                ),
                onClick: () => setIsEditing(true),
              },
            ]
      }
    >
      {!isEditing ? (
        <AppDescription
          column={1}
          labelStyle={{ width: 216 }}
          bordered={false}
          colon={false}
          size="middle"
        >
          {descriptionItems?.map((item, _index) => (
            <Descriptions.Item key={_index} label={item.label}>
              {item.value ?? '-'}
            </Descriptions.Item>
          ))}
        </AppDescription>
      ) : (
        <ProspectForm
          key={data?.id ?? 'new'}
          formRef={formRef}
          onSubmit={handleSubmit}
          disabled={isReadOnly}
          defaultValues={{
            title:
              find(titleOptions, (opt) => opt.value === data?.title) ?? null,
            dob: data?.DOB,
            forename: data?.forename ?? '',
            surname: data?.surname ?? '',
            agency: data?.agency
              ? {
                  label: data?.agency?.name ?? '',
                  value: data?.agency?.id ?? '',
                }
              : null,
            paymentCompany: data?.companyPayment
              ? {
                  label: data?.companyPayment?.companyName ?? '',
                  value: data?.companyPayment?.id ?? '',
                  fullData: data?.companyPayment ?? null,
                }
              : null,
            email: data?.email ?? '',
            phoneNumber: data?.phone ?? '',
            payRate: data?.payRate,
            payFrequency:
              find(
                defaultPaymentFrequency,
                (opt) => opt.value === data?.payFrequency,
              )?.value ?? '',
            paymentModel:
              find(
                defaultPaymentModel,
                (opt) => opt.value === data?.paymentModel?.[0],
              ) ?? null,
            businessType:
              find(
                defaultsBusinessTypeOptions,
                (opt) => opt.value === data?.businessType,
              ) ?? null,
          }}
        />
      )}
    </AppModalContainer>
  );
};

export default ViewProspectModal;
